import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, forwardRef, inject } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { BaseControlValueAccessor } from 'rev-shared/util/BaseControlValueAccessor';
import { ILanguageCode, TranscriptionLanguagesService } from 'rev-shared/media/transcription/TranscriptionLanguages.Service';
import { IVbLanguageTag } from 'rev-shared/ui/ngTagsInput/Ngx/VbLanguagesTagsInput.Component';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { RevIQService } from 'rev-shared/revIQ/RevIQ.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { VbDropdownMenuModule } from 'rev-shared/ui/dropdown-menu/VbDropdownMenu.Module';
import { VbTagsInputModule } from 'rev-shared/ui/ngTagsInput/Ngx/VbTagsInput.Module';
import { VbToggleButtonComponent } from 'rev-shared/ui/buttons/VbToggleButton.Component';

import { GenerateMetadataOption, IMetadataFields, ITranscribeAndAiMetadata } from './VideoAiMetadataTypes';
import { getPreferredTranscribeLanguage } from './TranscribeLanguagePreference';

import styles from './VideoTranscribeAndAiMetadata.Component.module.less';

let videoTAiIndex = 0;

interface ITranscribe {
	isLocked?: boolean,
	isEnabled?: boolean,
	languageCode?: string,
	languageName?: string,
	isNotPermitted?: boolean,
	additionalSubtitleLanguages?: IVbLanguageTag[];
}

const fieldNameKeys = {
	[GenerateMetadataOption.TITLE]: 'Title',
	[GenerateMetadataOption.DESCRIPTION]: 'Description',
	[GenerateMetadataOption.TAGS]: 'Tags',
	[GenerateMetadataOption.CHAPTERS]: 'Media_Videos_Chapters'
};

@Component({
	selector: 'video-transcribe-and-ai-metadata',
	standalone: true,
	imports: [
		BsDropdownModule,
		CommonModule,
		FormsModule,
		TooltipModule,
		TranslateModule,
		VbDropdownMenuModule,
		VbTagsInputModule,
		VbToggleButtonComponent
	],
	templateUrl: './VideoTranscribeAndAiMetadata.Component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => VideoTranscribeAndAiMetadataComponent),
			multi: true
		}
	],
})
export class VideoTranscribeAndAiMetadataComponent extends BaseControlValueAccessor<ITranscribeAndAiMetadata> implements OnInit {
	@Input() public containerClass: { dropdownMenu: string };
	@Input() public overwriteFields: IMetadataFields = {};

	private readonly TranscriptionLanguagesService = inject(TranscriptionLanguagesService);
	public readonly TranslateService = inject(TranslateService);
	private readonly RevIQService = inject(RevIQService);
	private readonly accountId = inject(UserContextService).getAccount().id;
	public readonly mediaFeatures = inject(MediaFeaturesService).accountFeatures;
	public readonly isMetadataGenerationPermitted = this.mediaFeatures.aiSettings.enableVideoMetadataGeneration;
	public readonly styles = styles;
	public readonly GenerateMetadatOption = GenerateMetadataOption;
	public readonly transcriptionLanguages = this.TranscriptionLanguagesService.loadAiServiceLanguages();
	public readonly translationLanguages = this.TranscriptionLanguagesService.loadAiServiceTranslationLanguages();
	public readonly enabledFields = [
		GenerateMetadataOption.TITLE,
		GenerateMetadataOption.DESCRIPTION,
		this.mediaFeatures.enableTags && GenerateMetadataOption.TAGS,
		GenerateMetadataOption.CHAPTERS
	].filter(Boolean);
	public readonly autoTranscribeOtherUploads = this.mediaFeatures.transcriptionSettings?.autoTranscribeOtherUploads;

	public videoTAiIndex = 0;
	public status: { [key: string]: boolean } = {};
	public revIQEligible = false;
	public transcribe: ITranscribe;
	public isMetadataGenerationDisabled: boolean = false;
	public allChecked: boolean = false;
	public fields: IMetadataFields = this.enabledFields.reduce((acc, field) => ({ ...acc, [field]: false }), {});
	public expand: boolean = false;
	public hasIqCredits: boolean;
	public whitelistedLanguages: ILanguageCode[] = [];

	public onTranscribeLanguageClick(lang: { code: string, name: string }): void {
		this.transcribe = {
			...this.transcribe,
			languageCode: lang.code,
			languageName: lang.name
		};

		this.updateAdditionalSubtitleLanguages();
		this.updateAiMetadata();
		this.onValueChange();
	}

	public onMetadataChange(): void {
		this.updateAiMetadata();
		this.onValueChange();
	}

	public updateAllMetadataFields(value: boolean): void {
		this.setAllMetadataFields(value);
		this.onValueChange();
	}

	public updateMetadataField(field: GenerateMetadataOption, value: boolean): void {
		this.fields[field] = value;
		this.allChecked = Object.values(this.fields).every(Boolean);
		this.onValueChange();
	}

	public getFieldLabel(field: GenerateMetadataOption): string {
		return this.TranslateService.instant(fieldNameKeys[field]) +
			(this.overwriteFields[field] ? ` (${this.TranslateService.instant('Overwrite')})` : '');
	}

	public ngOnInit(): void {
		this.videoTAiIndex = ++videoTAiIndex;
		if (!this.mediaFeatures.transcriptionSettings.isEnabledRevTranscription) {
			return;
		}

		this.status = { initializing: true };
		Promise.all([
			this.RevIQService.isRevIQCreditsValid(),
			this.RevIQService.isRevIQEligible(this.accountId),
		])
			.then(([creditsValid, transcriptionEnabled]) => {
				this.hasIqCredits = creditsValid;
				const isAuthorized = this.RevIQService.isRevIQAuthorized();
				const isRevIQEligible = creditsValid && transcriptionEnabled && isAuthorized;

				if (!isRevIQEligible) {
					this.transcribe = {
						isLocked: true,
						isEnabled: false,
						isNotPermitted: true
					};
					this.updateAiMetadata();
				} else {
					this.initForm();
					if (this.transcribe.isEnabled) {
						this.onValueChange();
					}
				}
				this.status = { ready: true };
			})
			.catch(e => console.error('Error initializing VideoTranscribeAndAiMetadataComponent', e));
	}

	private initForm(): void {
		const initialLanguageId = this.autoTranscribeOtherUploads ? this.mediaFeatures.transcriptionSettings?.defaultLanguage : this.value?.transcribeLanguageId;
		const language = getPreferredTranscribeLanguage(this.transcriptionLanguages as any, initialLanguageId);
		this.transcribe = {
			isLocked: this.autoTranscribeOtherUploads,
			isEnabled: this.autoTranscribeOtherUploads || !!this.value?.transcribeLanguageId,
			languageCode: language.code,
			languageName: language.name
		};

		if (!this.autoTranscribeOtherUploads) {
			this.transcribe.additionalSubtitleLanguages = this.value?.additionalSubtitleLanguages
				?.map(lang => this.translationLanguages.find(tlang => tlang.code === lang));
		}
		this.updateAdditionalSubtitleLanguages();

		this.updateAiMetadata();
		if (!this.isMetadataGenerationDisabled) {
			this.value?.metadataGenerationFields?.forEach(field => {
				this.updateMetadataField(field, true);
			});
			this.expand = true;
		}
	}

	private updateAdditionalSubtitleLanguages(): void {
		const uniqueLanguagesMap = new Map(
			this.translationLanguages
				.map(lang => {
					if (this.autoTranscribeOtherUploads
						&& (this.mediaFeatures.transcriptionSettings?.defaultTranslationLanguages ?? []).includes(lang.code)
					) {
						return { ...lang, readonly: true };
					}
					const isAlreadyAdded = this.transcribe.additionalSubtitleLanguages?.some(existingLang => existingLang.code === lang.code);
					return isAlreadyAdded ? lang : null;
				})
				.filter(lang => lang && lang.code !== this.transcribe.languageCode)
				.map(lang => [lang!.code, lang!])
		);

		this.transcribe.additionalSubtitleLanguages = Array.from(uniqueLanguagesMap.values());

		this.whitelistedLanguages = this.translationLanguages.filter(tlang => tlang.code !== this.transcribe.languageCode);
	}

	private getSelectedMetadataFields(): GenerateMetadataOption[] {
		return Object.entries(this.fields)
			.filter(([_, value]) => value)
			.map(([key]) => key as GenerateMetadataOption);
	}

	private updateAiMetadata(): void {
		if (!this.isMetadataGenerationPermitted) {
			this.isMetadataGenerationDisabled = true;
			return;
		}

		this.isMetadataGenerationDisabled = !this.transcribe.isEnabled ||
			this.transcribe.languageCode !== 'en' &&
			this.transcribe.additionalSubtitleLanguages?.every(lang => lang.code !== 'en');

		// metadata toggles must always be manually enabled by user
		if (this.isMetadataGenerationDisabled) {
			this.setAllMetadataFields(false);
		}
	}

	private onValueChange(): void {
		if (!this.transcribe.isEnabled) {
			this.updateModelValue(undefined);
			return;
		}
		this.updateModelValue({
			transcribeLanguageId: this.transcribe.languageCode,
			metadataGenerationFields: this.getSelectedMetadataFields(),
			additionalSubtitleLanguages: this.transcribe.additionalSubtitleLanguages.map(lang => lang.code)
		});
	}

	private setAllMetadataFields(value: boolean): void {
		this.fields = {
			[GenerateMetadataOption.TITLE]: value,
			[GenerateMetadataOption.DESCRIPTION]: value,
			[GenerateMetadataOption.TAGS]: value,
			[GenerateMetadataOption.CHAPTERS]: value
		};
		this.allChecked = value;
	}
}
